export const isValidURL = (str: string): boolean => {
  try {
    new URL(str); // Try creating a URL
    return true; // Success means it's a valid URL
  } catch {
    return false; // Any error means it's invalid
  }
};

export const getLocalFileUrl = (filePath: string): string => {
  const currentDomain = window.location.origin;

  // Combine the domain and file path
  const fullURL = new URL(filePath, currentDomain);

  // Output the result
  return fullURL.href;
};

// Detect AWS S3-style endpoints broadly, including:
// - Virtual-hosted: https://bucket.s3.amazonaws.com/key
// - Regional virtual-hosted: https://bucket.s3.us-west-2.amazonaws.com/key
// - Path-style: https://s3.amazonaws.com/bucket/key, https://s3.us-west-2.amazonaws.com/bucket/key
// - Accelerate/Dualstack: https://bucket.s3-accelerate.amazonaws.com, https://s3.dualstack.us-east-1.amazonaws.com/bucket/key
// - Website: https://bucket.s3-website-us-east-1.amazonaws.com
// - China: *.amazonaws.com.cn
// - Object Lambda / accesspoint / vpce and other multi-label variants
//   e.g., https://s3-object-lambda.us-west-2.amazonaws.com, https://s3.us-west-2.vpce.amazonaws.com
export const isAwsS3Url = (input: string): boolean => {
  console.log('🔍 isAwsS3Url called with:', {
    input: input,
    inputType: typeof input,
    inputLength: input ? String(input).length : 0,
    inputFirstChars: input ? String(input).substring(0, 100) : 'null/undefined',
  });

  try {
    const url = new URL(input);
    const host = url.hostname.toLowerCase();

    console.log('✅ URL parsing succeeded:', {
      originalInput: input,
      hostname: url.hostname,
      hostnameLower: host,
      protocol: url.protocol,
      pathname: url.pathname,
      search: url.search?.substring(0, 100) + (url.search?.length > 100 ? '...' : ''),
      href: url.href?.substring(0, 150) + (url.href?.length > 150 ? '...' : ''),
    });

    // More permissive regex to catch edge cases in signed URLs
    // Matches any hostname containing s3 and amazonaws.com
    const s3Host = /(?:^|\.|\b)s3(?:[.-][\w-]*)*\.amazonaws\.com(?:\.cn)?$/i;
    const regexResult = s3Host.test(host);

    console.log('🎯 Regex test result:', {
      hostname: host,
      regexPattern: s3Host.toString(),
      regexResult: regexResult,
      matches: host.match(s3Host),
    });

    return regexResult;
  } catch (e) {
    const error = e as Error;
    console.log('❌ URL parsing failed, using fallback:', {
      error: error.message,
      errorType: error.name,
      originalInput: input,
    });

    // If URL parsing fails, use a more permissive string-based approach
    // This handles very long URLs or malformed URLs that might cause parsing issues
    const inputStr = String(input).toLowerCase();

    console.log('🔄 Fallback string analysis:', {
      inputStr: inputStr.substring(0, 150) + (inputStr.length > 150 ? '...' : ''),
      inputStrLength: inputStr.length,
    });

    // Look for amazonaws.com with s3 anywhere in the hostname portion
    const fallbackRegex = /(?:https?:\/\/[^/]*)?[^/]*s3[^/]*\.amazonaws\.com(?:\.cn)?/i;
    const fallbackResult = fallbackRegex.test(inputStr);

    console.log('🎯 Fallback regex test result:', {
      regexPattern: fallbackRegex.toString(),
      regexResult: fallbackResult,
      matches: inputStr.match(fallbackRegex),
    });

    return fallbackResult;
  }
};
